import { VitalSignType, VitalSigns } from '@binah/web-sdk';
import { VitalSignTypes } from './constants';
import { CategorizedReadingData, ReadingData } from '../types';
import { ScanReadings } from 'services/face-scan/type';

export const getRangeWithCategory = (colorRange: any, categories: any) => {
  function getCategory(range: any) {
    for (let key in categories) {
      if (categories[key] instanceof Array) {
        let categoryRange = categories[key];
        if (range.length === 1 && range[0] === categoryRange[0]) {
          return key;
        } else if (
          range.length === 2 &&
          range[0] >= categoryRange[0] &&
          range[1] <= categoryRange[1]
        ) {
          return key;
        }
      } else if (range.length === 1 && range[0] === categories[key]) {
        return key;
      }
    }
    return null;
  }

  const result = colorRange.map((item: any) => {
    let category = getCategory(item.range);
    return {
      ...item,
      category: category,
    };
  });

  return result;
};

export const formatHealthReading = (vitals: VitalSigns) => {
  let readings: Record<
    string,
    string | { type: number; value: number; confidence?: { level: number } }
  > = {};

  Object.entries(VitalSignTypes).forEach(([vitalKey, type]) => {
    const vital = VitalSignType[vitalKey as keyof typeof VitalSignType];

    if (vitals?.[vital]) {
      readings[vitalKey] = {
        type,
        value: vitals[vital].value,
        ...(vitals[vital]?.confidenceLevel && {
          confidence: { level: vitals[vital].confidenceLevel },
        }),
      };
    } else {
      if (vital === VitalSignType.OXYGEN_SATURATION) {
        readings[vitalKey] = { type, value: 96 };
      } else {
        readings[vitalKey] = 'N/A';
      }
    }
  });
  return readings;
};

export function getScanHexColor(
  value: number,
  ranges: { range: any[]; color: string }[]
) {
  for (const rangeObj of ranges) {
    const range = rangeObj.range;
    if (range.length === 1) {
      const operator = range[0].slice(0, 1);
      const number = parseFloat(range[0].slice(1));
      if (
        (operator === '<' && value < number) ||
        (operator === '>' && value > number)
      ) {
        return { range, color: rangeObj.color };
      }
    } else {
      if (value >= range[0] && value <= range[1]) {
        return { range, color: rangeObj.color };
      }
    }
  }
  return { range: '', color: '' };
}

export const formatScanData = (scanDetails: any, index: number) => {
  const reading = scanDetails?.readings?.[index];
  const categoryConfig = scanDetails?.category_config;
  const scaleValueMapping = scanDetails?.scale_value_mapping;
  const categories = categoryConfig ? Object.values(categoryConfig) : [];

  const readingData = reading?.reading_data;

  if (!readingData || !Object.keys(readingData).length) return [];

  const details = [];
  for (const key in readingData) {
    const config = scanDetails?.config?.[key];
    if (config) {
      const categoryRange: any = categories?.find(
        (cat: any) =>
          (key === 'OXYGEN_SATURATION' && cat?.Parameter === 'O2 Saturation') ||
          cat?.Parameter?.toLowerCase() === config.display.toLowerCase() ||
          cat?.Parameter?.toLowerCase() === key.toLowerCase()
      );

      let scaleCriteria: any;
      if (categoryRange) {
        scaleCriteria = categoryRange?.['Scale Criteria'];
        if ('Male' in scaleCriteria) {
          scaleCriteria = scaleCriteria['Male'];
        }
        if ('[0,65]' in scaleCriteria) {
          scaleCriteria = scaleCriteria['[0,65]'];
        }
      }

      const data = readingData[key];

      let value = data.value;
      if (['Diabetes Risk', 'Hypertension Risk'].includes(config.display)) {
        value = scaleValueMapping[data.value];
      }

      let color = '';
      let valueRange: any = '';
      const colorRangeArr = config?.color_range?.variations?.age
        ? Object.values(config?.color_range?.variations?.age)[0]
        : config?.color_range;

      if (!colorRangeArr?.length) {
        color =
          scanDetails?.col_val?.Default?.[key]?.[value] ||
          scanDetails?.col_val?.Default?.[key]?.Normal ||
          scanDetails?.col_val?.Default?.[key]?.Moderate ||
          scanDetails?.col_val?.Default?.[key]?.Moderada ||
          '';
      } else {
        const hexColor = getScanHexColor(value, colorRangeArr);
        color = hexColor.color;
        valueRange = hexColor.range;
      }

      const colorRangeWithCategory =
        scaleCriteria && colorRangeArr.length
          ? getRangeCategory(colorRangeArr, scaleCriteria)
          : [];

      details.push({
        name: config?.display,
        value: ['Diabetes Risk', 'Hypertension Risk'].includes(config.display)
          ? data.value
          : value,
        unit: config?.unit,
        color,
        short_intro: config?.short_intro,
        long_intro: config?.long_intro,
        colorRange: colorRangeArr,
        category: data?.category,
        valueRange,
        key,
        colorRangeWithCategory,
      });
    }
  }

  const categorizedDetails: any = {};
  details.forEach((detail) => {
    if (['systolic', 'diastolic'].includes(detail.key)) {
      const isSystolic = detail.key === 'systolic';
      if ('Blood Pressure' in categorizedDetails) {
        categorizedDetails['Blood Pressure'] = {
          ...categorizedDetails['Blood Pressure'],
          categories: {
            ...categorizedDetails['Blood Pressure'].categories,
            [detail.key]: { ...detail },
          },
        };
      } else {
        categorizedDetails['Blood Pressure'] = {
          name: 'Blood Pressure',
          categories: {
            [detail.key]: { ...detail },
          },
        };
      }

      if (isSystolic) {
        categorizedDetails['Blood Pressure'] = {
          ...categorizedDetails['Blood Pressure'],
          long_intro: detail.long_intro,
          short_intro: detail.short_intro,
          key: 'BLOOD_PRESSURE',
          category: detail.category,
          color: detail.color,
          colorRange: detail.colorRange,
          colorRangeWithCategory: detail.colorRangeWithCategory,
        };
      }
    } else {
      categorizedDetails[detail.name] = detail;
    }
  });

  return Object.values(categorizedDetails);
};

export const getCategorizedScanData = ({
  healthReadings,
  selectedIndex,
}: {
  healthReadings: ScanReadings | undefined;
  selectedIndex: number | undefined;
}) => {
  const subCategorizedData: CategorizedReadingData = {};
  const scanData = (
    typeof selectedIndex === 'number'
      ? formatScanData(healthReadings, selectedIndex)
      : []
  ) as ReadingData[];

  if (scanData.length) {
    if (healthReadings?.sub_categorisation) {
      for (const [key, value] of Object.entries(
        healthReadings.sub_categorisation
      )) {
        value.forEach((item) => {
          const subCategorydata = scanData.find(
            (data) => data.key === item.vital_key
          );
          if (!subCategorydata) return;

          const subParameters: ReadingData[] = [];
          if (item.subParameters?.length) {
            item.subParameters.forEach((subParam) => {
              const subParamdata = scanData.find(
                (data) => data.key === subParam.vital_key
              );
              if (subParamdata) subParameters.push(subParamdata);
            });
          }

          const data = {
            display: item?.display ?? '',
            ...subCategorydata,
            subParameters,
          };
          subCategorizedData[key] = [
            ...(subCategorizedData[key] ? subCategorizedData[key] : []),
            data,
          ];
        });
      }
    }
  }
  return subCategorizedData;
};

function getRangeCategory(colorRange: any, categoryRange: any) {
  const uniqueRanges: any = [];

  for (const category in categoryRange) {
    const rangeVal = categoryRange[category];

    for (const colorDict of colorRange) {
      if (typeof rangeVal === 'string') {
        let colorDictRangeNum: number | undefined = undefined;
        if (colorDict.range.length === 1) {
          colorDictRangeNum = parseFloat(
            colorDict.range[0].toString().slice(1)
          );
          if (colorDict.range[0].includes('=')) {
            colorDictRangeNum = parseFloat(
              colorDict.range[0].toString().slice(2)
            );
          }

          if (rangeVal[0] === colorDict.range[0][0] && rangeVal[0] === '<') {
            if (parseFloat(rangeVal.slice(1)) <= colorDictRangeNum) {
              uniqueRanges.push({
                eng_category: category,
                range: [rangeVal],
                color: colorDict.color,
              });
            } else {
              uniqueRanges.push({
                eng_category: category,
                range: [colorDict.range[0]],
                color: colorDict.color,
              });
            }
          } else if (
            rangeVal[0] === colorDict.range[0][0] &&
            rangeVal[0] === '>'
          ) {
            if (parseFloat(rangeVal.slice(1)) >= colorDictRangeNum) {
              uniqueRanges.push({
                eng_category: category,
                range: [rangeVal],
                color: colorDict.color,
              });
            } else {
              uniqueRanges.push({
                eng_category: category,
                range: [colorDict.range[0]],
                color: colorDict.color,
              });
            }
          }
        } else {
          if (
            rangeVal[0] === '<' &&
            colorDict.range[0] < colorDict.range[1] &&
            colorDict.range[1] < parseFloat(rangeVal.slice(1))
          ) {
            uniqueRanges.push({
              eng_category: category,
              range: colorDict.range,
              color: colorDict.color,
            });
          } else if (
            rangeVal[0] === '<' &&
            colorDict.range[0] < parseFloat(rangeVal.slice(1)) &&
            parseFloat(rangeVal.slice(1)) <= colorDict.range[1]
          ) {
            uniqueRanges.push({
              eng_category: category,
              range: [colorDict.range[0], parseFloat(rangeVal.slice(1))],
              color: colorDict.color,
            });
          } else if (
            rangeVal[0] === '>' &&
            parseFloat(rangeVal.slice(1)) < colorDict.range[0] &&
            colorDict.range[0] < colorDict.range[1]
          ) {
            uniqueRanges.push({
              eng_category: category,
              range: colorDict.range,
              color: colorDict.color,
            });
          } else if (
            rangeVal[0] === '>' &&
            colorDict.range[0] <= parseFloat(rangeVal.slice(1)) &&
            parseFloat(rangeVal.slice(1)) < colorDict.range[1]
          ) {
            uniqueRanges.push({
              eng_category: category,
              range: [parseFloat(rangeVal.slice(1)), colorDict.range[1]],
              color: colorDict.color,
            });
          }
        }
      } else if (Array.isArray(rangeVal)) {
        if (colorDict.range.length === 2) {
          if (
            colorDict.range[0] <= rangeVal[0] &&
            rangeVal[0] <= colorDict.range[1] &&
            colorDict.range[1] <= rangeVal[1]
          ) {
            uniqueRanges.push({
              eng_category: category,
              range: [rangeVal[0], colorDict.range[1]],
              color: colorDict.color,
            });
          } else if (
            rangeVal[0] <= colorDict.range[0] &&
            colorDict.range[0] <= rangeVal[1] &&
            rangeVal[1] <= colorDict.range[1]
          ) {
            uniqueRanges.push({
              eng_category: category,
              range: [colorDict.range[0], rangeVal[1]],
              color: colorDict.color,
            });
          } else if (
            colorDict.range[0] <= rangeVal[0] &&
            rangeVal[1] <= colorDict.range[1]
          ) {
            uniqueRanges.push({
              eng_category: category,
              range: rangeVal,
              color: colorDict.color,
            });
          } else if (
            rangeVal[0] <= colorDict.range[0] &&
            colorDict.range[1] <= rangeVal[1]
          ) {
            uniqueRanges.push({
              eng_category: category,
              range: colorDict.range,
              color: colorDict.color,
            });
          } else if (
            colorDict.range[0] === rangeVal[0] &&
            colorDict.range[1] === rangeVal[1]
          ) {
            uniqueRanges.push({
              eng_category: category,
              range: rangeVal,
              color: colorDict.color,
            });
          }
        } else if (colorDict.range.length === 1) {
          let colorDictRangeNum = parseFloat(
            colorDict.range[0].toString().slice(1)
          );
          if (colorDict.range[0].includes('=')) {
            colorDictRangeNum = parseFloat(
              colorDict.range[0].toString().slice(2)
            );
          }

          if (colorDict.range[0][0] === '<') {
            if (rangeVal[0] < rangeVal[1] && rangeVal[1] < colorDictRangeNum) {
              uniqueRanges.push({
                eng_category: category,
                range: rangeVal,
                color: colorDict.color,
              });
            } else if (
              rangeVal[0] < colorDictRangeNum &&
              colorDictRangeNum < rangeVal[1]
            ) {
              uniqueRanges.push({
                eng_category: category,
                range: [rangeVal[0], colorDictRangeNum],
                color: colorDict.color,
              });
            }
          }
          if (colorDict.range[0][0] === '>') {
            if (colorDictRangeNum < rangeVal[0] && rangeVal[0] < rangeVal[1]) {
              uniqueRanges.push({
                eng_category: category,
                range: rangeVal,
                color: colorDict.color,
              });
            } else if (
              rangeVal[0] < colorDictRangeNum &&
              colorDictRangeNum < rangeVal[1]
            ) {
              uniqueRanges.push({
                eng_category: category,
                range: [colorDictRangeNum, rangeVal[1]],
                color: colorDict.color,
              });
            }
          }
        }
      }
    }
  }
  return uniqueRanges;
}
